import React, { createContext, useContext, useState, useEffect } from 'react';
import { SiteData, LoadingState, BlogPost } from '../types';
import { firebaseService } from '../services/firebaseService';

interface DataContextValue extends LoadingState {
  siteData: SiteData | null;
  blogPosts: BlogPost[];
  refetch: () => Promise<void>;
}

const DataContext = createContext<DataContextValue | null>(null);

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
  const [siteData, setSiteData] = useState<SiteData | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [siteData, blogPosts] = await Promise.all([
        firebaseService.getSiteData(),
        firebaseService.getBlogPosts()
      ]);
      
      setSiteData(siteData);
      setBlogPosts(blogPosts);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setSiteData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refetch = async () => {
    await loadData();
  };

  return (
    <DataContext.Provider value={{ siteData, blogPosts, loading, error, refetch }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = (): DataContextValue => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export default DataContext;
